import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers, type U2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { u2000TodayOrgYmd } from "../../../../../u2000Core/tools/U2000DateTimeHelpers";
import {
  luHandleAppiError,
  pickPaymentTypeLabel,
  u3125CanManagePayments,
  u3125ComputeBalanceToCollect,
  u3125FormatPaymentRemarkForDisplay,
  u3125IsCreditApplicationAutoPaymentRemark,
  u3125PaymentAmountWithinRemaining,
  u3125PaymentRemainingBalanceForInvoice,
  u3125SumPaymentGridTotals,
  u3125TaxAmountFieldLabels,
  type U3125InvoiceFormRow,
  type U3125InvoicePaymentGridRow,
  type U3125PaymentGridTotals,
  type U3125PaymentTypeInitItem,
  type U3125TaxRateInitItem,
} from "../../../../U3125Service";
import { NS, U3125ROLES, u3125FormatCurrency } from "../../../../U3125";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";
import { useU3125PaymentApiHandlers } from "../../../../U3125UseApiHandlers";
import { useU3125PaymentHandler } from "./U3125PaymentHandlers";
import U3125PaymentEditDlg from "./U3125PaymentEditDlg";
import U3125PaymentDeleteDlg from "./U3125PaymentDeleteDlg";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";

const PAYMENTS_GRID_FOOTER_ALIGN: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function U3125PaymentsGridFooterRow({
  gridStateHandlers,
  totals,
  totalLabel,
}: {
  gridStateHandlers: U2000GridStateHandlers<U3125InvoicePaymentGridRow>;
  totals: U3125PaymentGridTotals;
  totalLabel: string;
}) {
  const columnMap = new Map(gridStateHandlers.getColumns().map((col) => [col.name, col]));

  const footerAmount = (fieldName: keyof U3125PaymentGridTotals): string => u3125FormatCurrency(totals[fieldName]);

  return (
    <tr aria-label={totalLabel} className="text-sm">
      {columnMap.has("id") ? <td className="px-2 py-2.5 border-r border-gray-200" /> : null}
      {gridStateHandlers.getSelectedFields().map((fieldName) => {
        const column = columnMap.get(fieldName);
        if (!column) return null;

        const cellClass = `${PAYMENTS_GRID_FOOTER_ALIGN[column.align ?? "left"]} px-4 py-2.5 border-r last:border-r-0 border-gray-100 font-semibold text-gray-800 ${column.widthClass ?? "min-w-[100px]"}`;

        if (fieldName === "paymentDate") {
          return (
            <td key="paymentDate" colSpan={2} className={`text-left pl-4 py-2.5 border-r border-gray-100 font-semibold text-gray-800 ${column.widthClass ?? ""}`}>
              {totalLabel}
            </td>
          );
        }
        if (fieldName === "paymentTypeCode") return null;

        if (fieldName === "amountTotal") {
          return (
            <td key={fieldName} className={`${cellClass} text-right`}>
              {footerAmount("amountTotal")}
            </td>
          );
        }
        if (fieldName === "amountTaxable") {
          return (
            <td key={fieldName} className={`${cellClass} text-right`}>
              {footerAmount("amountTaxable")}
            </td>
          );
        }
        if (fieldName === "taxAmount1") {
          return (
            <td key={fieldName} className={`${cellClass} text-right`}>
              {footerAmount("taxAmount1")}
            </td>
          );
        }
        if (fieldName === "taxAmount2") {
          return (
            <td key={fieldName} className={`${cellClass} text-right`}>
              {footerAmount("taxAmount2")}
            </td>
          );
        }
        if (fieldName === "amountNonTaxable") {
          return (
            <td key={fieldName} className={`${cellClass} text-right`}>
              {footerAmount("amountNonTaxable")}
            </td>
          );
        }

        return <td key={fieldName} className={cellClass} />;
      })}
    </tr>
  );
}

function getColumnsDef(
  t: TFunction,
  paymentTypes: U3125PaymentTypeInitItem[],
  languageCode: string,
  taxRates: U3125TaxRateInitItem[],
  taxRateCode: string | null | undefined,
): U2000TableColumn[] {
  const taxLabels = u3125TaxAmountFieldLabels(taxRates, taxRateCode, languageCode, {
    taxAmount1: t("lblTaxAmount1", { ns: NS }),
    taxAmount2: t("lblTaxAmount2", { ns: NS }),
  });
  return [
    U2000IdColumnDef,
    {
      name: "paymentDate",
      label: t("lblPaymentDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[14%]",
      type: "string",
      align: "left",
      display: (_c: U2000TableColumn, value: unknown) => {
        if (value == null || value === "") return "";
        const s = String(value);
        return s.length >= 10 ? s.slice(0, 10) : s;
      },
    },
    {
      name: "paymentTypeCode",
      label: t("lblPaymentTypeCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[16%]",
      type: "string",
      align: "left",
      display: (_c: U2000TableColumn, value: unknown) => {
        const code = value == null ? "" : String(value);
        if (!code) return "";
        return pickPaymentTypeLabel(paymentTypes, code, languageCode);
      },
    },
    {
      name: "amountTotal",
      label: t("lblAmountTotal", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "amountTaxable",
      label: t("lblAmountTaxable", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "taxAmount1",
      label: taxLabels.taxAmount1,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "taxAmount2",
      label: taxLabels.taxAmount2,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "amountNonTaxable",
      label: t("lblAmountNonTaxable", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "remark",
      label: t("lblRemark", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[16%]",
      type: "string",
      align: "left",
      display: (_c, v) => u3125FormatPaymentRemarkForDisplay(v == null ? null : String(v)),
    },
  ];
}

interface U3125PaymentsTabBodyProps {
  parentFormHandlers: U3125FactureNestedParentHandlers & { getData: () => U3125InvoiceFormRow | null };
  paymentTypes: U3125PaymentTypeInitItem[];
  taxRates: U3125TaxRateInitItem[];
}

const U3125PaymentsTabBody: React.FC<U3125PaymentsTabBodyProps> = ({ parentFormHandlers, paymentTypes, taxRates }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const toast = useToast();
  const gridStateHandlers = useU2000GridStateHandlers<U3125InvoicePaymentGridRow>();

  const invoiceData = parentFormHandlers.getData?.() ?? null;
  const payments = invoiceData?.payments;

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const businessHandlers: U2000FormSBusinessHandlers<U3125InvoicePaymentGridRow> = useMemo(
    () => ({
      newRecord: () =>
        ({
          id: "",
          paymentDate: u2000TodayOrgYmd(),
          paymentTypeCode: "",
          amountTotal: 0,
          amountTaxable: 0,
          amountNonTaxable: 0,
          taxAmount1: null,
          taxAmount2: null,
          remark: "",
        }) as U3125InvoicePaymentGridRow,
      validateForm: (data) => {
        const result: Record<string, string> = {};
        if (!data?.paymentDate?.trim()) result.paymentDate = t("errPaymentDateRequired", { ns: NS });
        if (!data?.paymentTypeCode?.trim()) result.paymentTypeCode = t("errPaymentTypeRequired", { ns: NS });
        const invoice = parentFormHandlers.getData?.();
        if (invoice) {
          const remaining = u3125PaymentRemainingBalanceForInvoice(
            invoice,
            invoice.payments ?? [],
            taxRates,
            data?.id?.trim() || undefined,
          );
          if (!u3125PaymentAmountWithinRemaining(Number(data?.amountTotal ?? 0), remaining)) {
            result.amountTotal = t("errPaymentAmountExceedsRemaining", { ns: NS });
          }
        }
        return result;
      },
    }),
    [t, parentFormHandlers, taxRates],
  );
  const paymentFormStateHandlers = useU2000FormStateHandlers<U3125InvoicePaymentGridRow>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handlePaymentApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const paymentApi = useU3125PaymentApiHandlers({
    parentFormHandlers,
    onError: handlePaymentApiError,
  });

  const handler = useU3125PaymentHandler({
    gridStateHandlers,
    t,
    formStateHandlers: paymentFormStateHandlers,
    parentFormHandlers,
    onPersistCreate: paymentApi.createPayment,
    onPersistUpdate: paymentApi.updatePayment,
    onPersistDelete: paymentApi.deletePayments,
  });

  const invoiceStatus = invoiceData?.status;
  const canManagePayments = u3125CanManagePayments(invoiceStatus);
  const hasPersistedInvoice = Boolean(invoiceData?.id?.trim());

  useEffect(() => {
    const columns = getColumnsDef(t, paymentTypes, i18n.language, taxRates, invoiceData?.taxRateCode);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const raw = Array.isArray(payments) ? payments : [];
    const list = raw.map((p) => ({ ...p, id: p.id?.trim() || generateTemporaryId() }) as U3125InvoicePaymentGridRow);
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [t, payments, paymentTypes, i18n.language, taxRates, invoiceData?.taxRateCode]);

  const selectedPayment = handler.gridStateHandlers.getSelectedRows()[0] as U3125InvoicePaymentGridRow | undefined;
  const selectedIsAutoPayment = selectedPayment
    ? u3125IsCreditApplicationAutoPaymentRemark(selectedPayment.remark)
    : false;

  const disabledModify =
    !canManagePayments || !hasPersistedInvoice || handler.gridStateHandlers.getSelectedRows().length !== 1 || selectedIsAutoPayment;
  const disabledDelete =
    !canManagePayments || !hasPersistedInvoice || handler.gridStateHandlers.getSelectedRows().length === 0 || selectedIsAutoPayment;
  const disabledAdd = !canManagePayments || !hasPersistedInvoice;

  const balanceToCollect = useMemo(() => u3125ComputeBalanceToCollect(invoiceData), [invoiceData]);
  const showBalanceFooter = invoiceData?.documentNature === "STANDARD";

  const paymentGridTotals = useMemo(() => u3125SumPaymentGridTotals(Array.isArray(payments) ? payments : []), [payments]);

  const paymentsGridFooter = useMemo(() => {
    if (!Array.isArray(payments) || payments.length === 0) return undefined;
    return (
      <U3125PaymentsGridFooterRow
        gridStateHandlers={gridStateHandlers}
        totals={paymentGridTotals}
        totalLabel={t("lblPaymentsGridTotal", { ns: NS })}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers stable handler ref
  }, [payments, paymentGridTotals, t, gridStateHandlers]);

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledAdd,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: handler.onModify,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledModify,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, disabledAdd, disabledModify, disabledDelete, hasRole],
  );

  return (
    <>
      <U3125PaymentEditDlg
        u3125PaymentHandler={handler}
        parentFormHandlers={parentFormHandlers}
        paymentTypes={paymentTypes}
        taxRates={taxRates}
        languageCode={i18n.language}
        t={t}
        NS={NS}
      />
      <U3125PaymentDeleteDlg u3125PaymentHandler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} className="min-w-0 flex-1" />
        {showBalanceFooter ? (
          <span className="shrink-0 text-sm font-semibold text-foreground">
            {t("lblBalanceToCollect", { ns: NS })} : {u3125FormatCurrency(balanceToCollect)}
          </span>
        ) : null}
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3125InvoicePaymentGridRow)}
        allowRowSelection={canManagePayments}
        label={t("lblPaymentsGrid", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        tableFooter={paymentsGridFooter}
      />
    </>
  );
};

export default U3125PaymentsTabBody;
